import React, { useCallback, useMemo } from "react";
import { AHSeparator, Box, useBoxStyle } from "arkh";
import { WATaskSingleSettingsScreenProps } from "../WATaskSingleStack";

import {
  graphql,
  loadQuery,
  useLazyLoadQuery,
  useRelayEnvironment,
} from "react-relay";
import { RoutineNavigationListItem } from "./RoutineNavigationListItem";
import { SectionList } from "react-native";
import { WA_TASK_SINGLE_SETTINGS_SELECT_TASK_QUERY } from "./WATaskSingleSettingsSelectTask";
import { LazyQueryNetworkBoundrary } from "../../../../../../../components/mobile/QueryNetworkBoundrary";
import { WATaskSingleSettingsSelectRoutineQuery } from "./__generated__/WATaskSingleSettingsSelectRoutineQuery.graphql";
import { keyExtractorId } from "../../../../WidgetAdd/utils/keyExtractors";
import { WATaskSingleSettingsSelectTaskQuery } from "./__generated__/WATaskSingleSettingsSelectTaskQuery.graphql";
import { ListSectionHeaderTeam } from "./ListSectionHeaderTeam";

const WA_TASK_SINGLE_SETTINGS_SELECT_ROUTINE_QUERY = graphql`
  query WATaskSingleSettingsSelectRoutineQuery {
    store {
      id
      publicTeams {
        id
        ...ListSectionHeaderTeam_Team
        checklists {
          id
          ...RoutineNavigationListItem_Routine
        }
      }
    }
  }
`;

export function WATaskSingleSettingsSelectRoutine(
  props: WATaskSingleSettingsScreenProps<"WATaskSingleSettingsSelectRoutine">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWATaskSingleSettingsSelectRoutine
          {...props}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWATaskSingleSettingsSelectRoutine(
  props: WATaskSingleSettingsScreenProps<"WATaskSingleSettingsSelectRoutine"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WATaskSingleSettingsSelectRoutineQuery>(
    WA_TASK_SINGLE_SETTINGS_SELECT_ROUTINE_QUERY,
    {},
    { fetchKey: props.fetchKey }
  );

  const env = useRelayEnvironment();

  const sections = useMemo(() => {
    return data.store.publicTeams.flatMap((team) =>
      (team.checklists?.length ?? 0) > 0
        ? [
            {
              key: team.id,
              data: team.checklists ?? [],
              team,
            },
          ]
        : []
    );
  }, [data.store.publicTeams]);

  type Section = (typeof sections)[number];
  type Item = Section["data"][number];
  const renderSectionHeader = useCallback(
    ({ section }: { section: Section }) => (
      <ListSectionHeaderTeam team={section.team} />
    ),
    []
  );

  const renderSectionFooter = useCallback(
    () => <Box spaceBottom="large" />,
    []
  );

  const renderItem = useCallback(
    ({
      item,
      index,
      section,
    }: {
      section: Section;
      item: Item;
      index: number;
    }) => {
      return (
        <RoutineNavigationListItem
          isFirst={index === 0}
          isLast={index === (section.data.length ?? 0) - 1}
          onPress={() => {
            const qr = loadQuery<WATaskSingleSettingsSelectTaskQuery>(
              env,
              WA_TASK_SINGLE_SETTINGS_SELECT_TASK_QUERY,
              { routineId: item.id }
            );
            props.navigation.navigateDeprecated(
              "WATaskSingleSettingsSelectTask",
              {
                queryRef: qr,
              }
            );
          }}
          routine={item}
        />
      );
    },
    [env, props.navigation]
  );

  const contentContainerStyle = useBoxStyle({
    space: "medium",
    spaceTop: "medium",
  });

  return (
    <SectionList
      contentContainerStyle={contentContainerStyle}
      ItemSeparatorComponent={AHSeparator}
      style={{ flex: 1 }}
      sections={sections}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      renderSectionFooter={renderSectionFooter}
      keyExtractor={keyExtractorId}
    />
  );
}
